import React from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Box } from '@mui/material';
import { CssBaseline, StyledEngineProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import Theme from './Theme';
import ApiAdmin from './apis/apiAdmin';
import useEventListener from './hooks/useEventListener';
import { getHostPrefix } from './utils/config';
import DialogOutlet from './components/dialog/DialogOutlet';
import { ConfirmProvider } from './contexts/ConfirmContext';

var apiErrorAddSentFlag = false;

// /////////////////////////////////////////////////////////////////////////////////////////////////
const appClientEtag = {
  etag: '',
  start: Date.now() + 1000 * 60 * 5, // check on next focus (Do not check at onload (it can lead to forever load))
  interval: 1000 * 60 * 5, // 5 Min check
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
function removeEtagWeakValidator(etag) {
  if (!etag) return etag;
  if (etag.startsWith('W/')) etag = etag.replace('W/', '');

  return etag;
}

// /////////////////////////////////////////////////////////////////////////////////////////////////
window.onload = () => {
  const url = `${getHostPrefix(process.env.REACT_APP_PORTAL_NAME)}/index.html`;
  return fetch(url, { method: 'HEAD' })
    .then((response) => {
      if (response.status !== 200) {
        console.log(url, response.status, response.status);
        return '';
      }
      const etag = response.headers.get('etag');
      if (etag) {
        appClientEtag.etag = removeEtagWeakValidator(etag);
        appClientEtag.start = Date.now();
      }
    })
    .catch((err) => console.log(err));
};

const queryClient = new QueryClient();

// /////////////////////////////////////////////////////////////////////////////////////////////////
// Getting rid of deprecation messages!
const originalConsoleError = console.error;
console.error = (message, ...args) => {
  if (typeof message === 'string' && (message.includes('defaultProps will be removed') || message.includes('findDOMNode is deprecated'))) {
    return;
  }
  originalConsoleError.apply(console, [message, ...args]);
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
// Filter Google Maps warnings about mapId and map type styling
const originalConsoleWarn = console.warn;
console.warn = (message, ...args) => {
  if (typeof message === 'string' && message.includes('preregistered map type may not apply all custom styles when a mapId is present')) {
    return;
  }
  originalConsoleWarn.apply(console, [message, ...args]);
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
const App = (props) => {
  const { router } = props;

  useEventListener('error', (e) => {
    console.error('App Cought Error', e);
    if (!apiErrorAddSentFlag && e) {
      const event = {
        portalName: process.env.REACT_APP_PORTAL_NAME,
        message: e.message,
        filename: e.filename,
        lineno: e.lineno,
        colno: e.colno,
        stack: e.error?.stack,
      };
      ApiAdmin.apiUiErrorCreate(event).catch((err) => console.log(err));
    }
    apiErrorAddSentFlag = true;
  });

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={Theme()}>
        <CssBaseline enableColorScheme={true} />
        <QueryClientProvider client={queryClient}>
          <ToastContainer />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box
              sx={{
                paddingTop: 'env(safe-area-inset-top)',
                paddingBottom: 'env(safe-area-inset-bottom)',
                paddingLeft: 'env(safe-area-inset-left)',
                paddingRight: 'env(safe-area-inset-right)',
              }}
            >
              <DialogOutlet />
              <ConfirmProvider>
                <RouterProvider router={router} />
              </ConfirmProvider>
            </Box>
          </LocalizationProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default App;
